import { Status } from '@weave/schema-gen-ts/dist/schemas/sms/shared/v1/enums.pb';
import { EntityType } from '@weave/schema-gen-ts/dist/schemas/sms/shared/v2/enums.pb';
import { useMutation } from 'react-query';
import { Options } from '@frontend/fetch';
import { LimitedSchemaMutationOptions } from '@frontend/react-query-helpers';
import { useGetUnifiedThreadEnabled } from '../../utils';
import { useSMSDataV4QueryUpdaters } from '../../v4/query-updaters';
import { useUnifiedThreadUpdater } from '../../v4/query-updaters';
import { SMSMutationContext, useSMSDataV3QueryUpdaters } from '../query-updaters';
import { SchemaSMSDataV3Service } from '../service';
import { OptimisticUpdateStatusDetails, ResendSMSIO } from '../types';

export const getResendSMSMutationKey = () => ['V3', 'ResendSMS'] as const;

type MutationContext<C = unknown> = {
  internalContext?: SMSMutationContext;
  otherContext?: C;
};

type ExtendedResendSMSRequest = ResendSMSIO['input'] & {
  threadId?: string;
  locationId: string;
};

type UseResendSMSMutationArgs<
  E = unknown,
  C = unknown,
  RequestOverride extends ExtendedResendSMSRequest = ExtendedResendSMSRequest
> = {
  options?: LimitedSchemaMutationOptions<
    { input: ExtendedResendSMSRequest; output: ResendSMSIO['output'] },
    E,
    C,
    never,
    RequestOverride
  >;
  httpOptions?: Options;
  optimisticUpdate?: boolean;
};
/**
 * A hook that returns a mutation for the `ResendSMS` mutation endpoint.
 * It handles query invalidation for the relevant query endpoints internally.
 * @param options (optional) The options to pass to `useMutation`.
 * @param httpOptions (optional) The http options to pass to the schema function.
 * @param optimisticUpdate (optional) Whether to perform an optimistic update. Defaults to `false`.
 */
export const useResendSMSMutation = <
  E = unknown,
  C = unknown,
  RequestOverride extends ExtendedResendSMSRequest = ExtendedResendSMSRequest
>({
  options,
  httpOptions,
  optimisticUpdate = false,
}: UseResendSMSMutationArgs<E, C | undefined, RequestOverride> = {}) => {
  const { updateSMS } = useSMSDataV3QueryUpdaters();
  const v4QueryUpdaters = useSMSDataV4QueryUpdaters({ httpOptions });
  const unifiedThreadUpdater = useUnifiedThreadUpdater({ httpOptions });
  const getUnifiedThreadEnabled = useGetUnifiedThreadEnabled();

  return useMutation<ResendSMSIO['output'], E, RequestOverride, MutationContext<C>>({
    mutationKey: getResendSMSMutationKey(),
    mutationFn: ({ threadId, ...req }) => SchemaSMSDataV3Service.ResendSMS(req, httpOptions),
    ...options,
    onMutate: async (request) => {
      const internalContext: SMSMutationContext = {
        revertOptimisticUpdate: () => {},
      };
      if (optimisticUpdate) {
        if (request.threadId) {
          if (getUnifiedThreadEnabled) {
            const upsertThreadResult = await unifiedThreadUpdater.upsertThreadItem({
              matchValues: {
                threadItemId: request.smsId,
                threadId: request.threadId,
                locationId: request.locationId,
                entityType: EntityType.ENTITY_TYPE_SMS, // resend applies only for sms thread item.
              },
              updater: (threadItem) => {
                if (threadItem && 'smsMessage' in threadItem) {
                  return {
                    smsMessage: {
                      ...threadItem.smsMessage,
                      status: Status.STATUS_NOT_SENT,
                      statusDetails: OptimisticUpdateStatusDetails.SENDING,
                    },
                  };
                }
                return threadItem;
              },
              isOptimistic: true,
            });
            internalContext.revertOptimisticUpdate = () => {
              upsertThreadResult.revertCacheUpdate();
            };
          } else {
            const updateSmsV3Result = updateSMS({
              matchValues: {
                id: request.smsId,
                threadId: request.threadId,
                locationId: request.locationId,
              },
              newValues: {
                status: Status.STATUS_NOT_SENT,
                statusDetails: OptimisticUpdateStatusDetails.SENDING,
              },
            });
            const upsertSmsV4Result = await v4QueryUpdaters.upsertSms({
              matchValues: {
                smsId: request.smsId,
                threadId: request.threadId,
                locationId: request.locationId,
              },
              updater: (sms) =>
                sms
                  ? { ...sms, status: Status.STATUS_NOT_SENT, statusDetails: OptimisticUpdateStatusDetails.SENDING }
                  : undefined,
              isOptimistic: true,
            });
            internalContext.originalSMS = updateSmsV3Result.originalSMS;
            internalContext.revertOptimisticUpdate = () => {
              updateSmsV3Result.revertOptimisticUpdate?.();
              upsertSmsV4Result.revertCacheUpdate();
            };
          }
        }
      }

      return {
        internalContext,
        otherContext: await options?.onMutate?.(request),
      };
    },
    onSuccess: async (response, request, context) => {
      if (getUnifiedThreadEnabled && response.sms.threadId) {
        // Use unified thread updater when flag is enabled
        await unifiedThreadUpdater.upsertThreadItem({
          matchValues: {
            threadItemId: request.smsId,
            threadId: response.sms.threadId,
            locationId: request.locationId,
            entityType: EntityType.ENTITY_TYPE_SMS, // resend applies only for sms thread item.
          },
          updater: () => ({
            smsMessage: response.sms,
          }),
          isOptimistic: false,
        });
        unifiedThreadUpdater.invalidateListUnifiedThreads();
      } else {
        updateSMS({
          matchValues: {
            id: response.sms.id,
            threadId: response.sms.threadId,
            locationId: response.sms.locationId,
          },
          newValues: response.sms,
        });
        v4QueryUpdaters.upsertSms({
          matchValues: {
            smsId: request.smsId,
            threadId: response.sms.threadId,
            locationId: request.locationId,
          },
          updater: () => response.sms,
          isOptimistic: false,
        });
      }

      return options?.onSuccess?.(response, request, context?.otherContext);
    },
    onError: (error, request, context) => {
      if (optimisticUpdate && context?.internalContext?.originalSMS) {
        updateSMS({
          matchValues: {
            id: request.smsId,
            threadId: request.threadId,
            locationId: request.locationId,
          },
          newValues: context.internalContext.originalSMS,
        });
        context.internalContext.revertOptimisticUpdate?.();
      }
      return options?.onError?.(error, request, context?.otherContext);
    },
    onSettled: (response, error, request, context) => {
      // Only pass context of type C into provided `onSettled` option
      return options?.onSettled?.(response, error, request, context?.otherContext);
    },
  });
};
